import ApiErrorBanner from '@/components/ApiErrorBanner';
import Lookup from '@/components/UI/Lookup/Lookup';
import { toNumericId } from '@/helpers';
import { _isNil, _isNotBlank, _isNotEmpty } from '@/littledash';
import type { Animal } from '@/model/Animal.model';
import type { ID } from '@/model/Common.model';
import InVivoError from '@/model/InVivoError.ts';
import { ApiService } from '@/support/ApiService';
import { notAborted, useAbortController } from '@/support/Hooks/fetch/useAbortController';
import Http from '@/support/http';
import { RouteService } from '@/support/RouteService';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { ErrorMessage } from '@hookform/error-message';
import { type FC, useState } from 'react';
import { Controller, useForm } from 'react-hook-form-latest';
import { useParams } from 'react-router-dom';

interface CageOption {
  label: string;
  value: ID;
}

interface MoveSubjectForm {
  cage: CageOption;
}

interface MoveSubjectProps {
  animal: Animal;
  handleCallback: (text?: string) => Promise<void>;
  closeModal: () => void;
}

export const MoveSubject: FC<MoveSubjectProps> = ({ animal, handleCallback, closeModal }) => {
  const { newAbortController } = useAbortController();
  const { id: studyId } = useParams();

  const [requestError, setRequestError] = useState<Error | false>(false);

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    control,
  } = useForm<MoveSubjectForm>({
    mode: 'onChange',
  });

  const loadOptions = async ({ inputValue, page }: { inputValue: string; page: string }) => {
    try {
      const {
        data: { data, meta },
      } = await Http.get(
        RouteService.legacyApi({
          apiRoute: 'studies.cages.list',
          path: { studyId },
          query: { page, ...(_isNotBlank(inputValue) ? { query: inputValue } : {}) },
        }).url.href,
        {
          signal: newAbortController().signal,
        }
      );
      const { current_page: currentPage, last_page: lastPage } = meta;
      const hasMore = currentPage < lastPage;
      const options: Array<CageOption> = data.map((item: { name: string; id: ID }) => ({
        label: item.name,
        value: item.id,
      }));
      return {
        options,
        hasMore,
      };
    } catch (error) {
      if (notAborted(error)) {
        ExceptionHandler.captureException(
          new InVivoError('Could not load study cages', {
            cause: error,
            slug: 'move-subjects-load-cages',
          })
        );
      }
    }
  };

  const onSubmit = async (form: MoveSubjectForm) => {
    setRequestError(false);
    try {
      await ApiService.call({
        endpoint: 'PATCH /api/v1/animals',
        body: { animals: [{ id: toNumericId(animal.id), cage_id: toNumericId(form.cage.value) }] },
        options: { onError: { throw: true, toast: false, slug: 'move-subject-bulk-update' } },
      });
      handleCallback();
    } catch (e) {
      setRequestError(e as Error);
    }
  };

  return (
    <ModalContainer size="narrow">
      <ModalHeader title="Move animal to new cage" closeModal={closeModal} className="pa3 bb b--moon-gray" />
      <form data-test-component="MoveSubject" data-test-element="form" onSubmit={handleSubmit(onSubmit)}>
        <div className="pa3" data-test-element="body">
          {requestError !== false && <ApiErrorBanner error={requestError} />}
          <label htmlFor="cage" data-testid="cage-label">
            Select a cage to move this animal to
          </label>
          <Controller
            control={control}
            name="cage"
            rules={{ validate: (cage: CageOption | undefined) => (_isNil(cage) ? 'Please select a cage' : true) }}
            render={({ field: { onChange } }) => (
              <Lookup
                name="cage"
                inputId="cage"
                placeholder="Search or select..."
                loadOptions={loadOptions}
                handleSelect={onChange}
                disabled={isSubmitting}
              />
            )}
          />
          <ErrorMessage
            errors={errors}
            name="cage"
            render={({ message }) => <p className="f6 red db pt3 pl1">{message}</p>}
          />
        </div>
        <div className="pa3 bt b--moon-gray">
          <ModalActions
            onSubmit={handleSubmit(onSubmit)}
            onCancel={closeModal}
            cancelBtnText="Close"
            submitButtonProps={{ disabled: isSubmitting || _isNotEmpty(errors), loading: isSubmitting }}
          />
        </div>
      </form>
    </ModalContainer>
  );
};
